/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { LogoutOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import {
    Box, Button, CardContent, ClickAwayListener, Grid, IconButton,
    Paper, Popper, Stack, Tab, Tabs, Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "cms/auth/hooks/use-auth";
import MainCard from "components/MainCard";
import { SVGIcons } from "cms/icons/svg-icons";
import Transitions from "components/Transitions";
import { useRef, useState } from "react";
import { ProfileTab } from "./ProfileTab";
import { SettingTab } from "./SettingTab";
import { ProfileTabPanel } from "./tab-panel";

const a11yProps = (index) => {
    return {
        id: `profile-tab-${index}`,
        "aria-controls": `profile-tabpanel-${index}`,
    };
};

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {

    const { user, logout } = useAuth();
    const theme = useTheme();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(0);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleLogout = async () => {
        logout();
    };
    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleChange = (event, changedValue) => {
        setValue(changedValue);
    };

    // const iconBackColorOpen = 'grey.300'
    // const avatarBackColor = theme.palette['accent'].main // theme.palette.secondary.main
    // const initials = user && user.initials ? user.initials : undefined
    const name = user && user.name;
    // const username = user && user.username
    const email = user && user.email;
    const avatar = <SVGIcons.common.Avatar />;

    return (
        <>
            <Button
                aria-haspopup='true'
                aria-label='open profile'
                ref={anchorRef}
                onClick={handleToggle}
                color='neutral'
                // variant='outlined'
                startIcon={avatar}
                className={`profile-button ${open ? "open" : ""}`}
            >
                {user && user.firstname}
            </Button>
            <Popper
                placement='bottom-end'
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                sx={{ zIndex: "999" }}
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 9],
                            },
                        },
                    ],
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions type='fade' in={open} {...TransitionProps}>
                        {open && (
                            <Paper
                                sx={{
                                    boxShadow: theme.customShadows.z1,
                                    width: 290,
                                    minWidth: 240,
                                    maxWidth: 290,
                                    [theme.breakpoints.down("md")]: {
                                        maxWidth: 250,
                                    },
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard elevation={0} border={false} content={false}>
                                        <CardContent sx={{ px: 2.5, pt: 3 }}>
                                            <Grid container >
                                                <Grid item sx={{
                                                    display: "flex",
                                                    justifyContents: "space-between",
                                                    alignItems: "center",
                                                    flexWrap: "nowrap",
                                                    gap: "1rem",
                                                }}
                                                >
                                                    <Stack direction='row' spacing={1.25} alignItems='center'>
                                                        {avatar}
                                                        <Stack>
                                                            <Typography variant='h6'>{name}</Typography>
                                                            <Typography variant='body2' color='textSecondary'>
                                                                {email}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                    <IconButton
                                                        size='large'
                                                        color='secondary'
                                                        onClick={handleLogout}
                                                    >
                                                        <LogoutOutlined />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                        {open && (
                                            <>
                                                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                                    <Tabs
                                                        variant='fullWidth'
                                                        value={value}
                                                        onChange={handleChange}
                                                        aria-label='profile tabs'
                                                    >
                                                        <Tab
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                textTransform: "capitalize",
                                                            }}
                                                            icon={
                                                                <UserOutlined
                                                                    style={{
                                                                        marginBottom: 0,
                                                                        marginRight: "10px",
                                                                    }}
                                                                />
                                                            }
                                                            label='Profile'
                                                            {...a11yProps(0)}
                                                        />
                                                        <Tab
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                textTransform: "capitalize",
                                                            }}
                                                            icon={
                                                                <SettingOutlined
                                                                    style={{
                                                                        marginBottom: 0,
                                                                        marginRight: "10px",
                                                                    }}
                                                                />
                                                            }
                                                            label='Setting'
                                                            {...a11yProps(1)}
                                                        />
                                                    </Tabs>
                                                </Box>

                                                <ProfileTabPanel value={value} index={0} dir={theme.direction}>
                                                    <ProfileTab />
                                                </ProfileTabPanel>

                                                <ProfileTabPanel value={value} index={1} dir={theme.direction}>
                                                    <SettingTab />
                                                </ProfileTabPanel>
                                            </>
                                        )}
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        )}
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export { Profile };
